import { FetchError } from "@/utils/error";

export type TPlan = {
  id: number;
  name: string;
  description: string;
  thirdparty_id: string | null;
};

export type TUser = {
  id: number;
  username: string;
  display_name: string;
  link_words: string;
  profile_image: string;
  /** 公司 logo（簡報頁尾用；未設定時以公司名稱文字呈現） */
  logo?: string | null;
  organization: string | null | undefined;
  chatbot_id: string | null | undefined;
  language: string;
  subscription?: {
    start_at: string;
    end_at: string;
  };
  address: string;
  phone: string;
  tax_id?: string;
  fax?: string;
  quote_valid_days?: number;
  context: string;
  opening: string;
  plan?: TPlan | null;
  custom_styles: TCustomStyles;
  is_first_login: boolean;
  // 業務估價 v2 功能開關（後台切換）
  estimate_v2_enabled?: boolean;
  portfolios?: TPortfolio[];
  is_superuser?: boolean;
  know_how?: string;
};

export type TCustomStyles = {
  theme: "light" | "dark";
  primary_color: string;
  secondary_color: string;
};

export type LoginData = {
  username: string;
  password: string;
};

export type RegisterData = {
  genie_name: string;
  username: string;
  password: string;
};

export type TCustomSpace = string;
export type TCustomTheme = {
  id: number;
  display_name: string;
  data: {
    input_image_url: string;
    prompt: string;
  };
};

export type TDesignerImageGenerationConfig = {
  custom_spaces: TCustomSpace[];
  custom_themes: TCustomTheme[];
};

export type TPortfolio = {
  id: number;
  name: string;
  type: string;
  description: string;
  updated_at: string;
  created_at: string;
  works: TWorks[];
};

export type TClientRequestsStatistics = {
  designer_id: number;
  designer_email: string;
  designer_display_name: string;
  completed: number;
  completed_with_full_contact: number;
  completed_other: number;
  uncompleted: number;
  uncompleted_with_full_contact: number;
  uncompleted_other: number;
  designer_generated: number;
  stability_gen_and_controlnet: number;
  /** 2D to 3D 使用次數（FloorplanMassingJob 數） */
  floorplan_massing: number;
  total: number;
};

export type TAiUsageFeature = "text" | "render" | "floorplan_3d" | "library_3d";

export type TAiUsageCost = Record<TAiUsageFeature, number> & { total: number };

export type TAiUsageMonthly = {
  currency: string;
  /** 由新到舊，格式 YYYY-MM（台灣時間） */
  months: string[];
  features: { key: TAiUsageFeature; label: string }[];
  totals: (TAiUsageCost & { month: string })[];
  designers: {
    /** null＝無法歸戶的系統呼叫 */
    designer_id: number | null;
    designer_email: string;
    designer_display_name: string;
    total: number;
    months: Record<string, TAiUsageCost>;
  }[];
  /** 價格表查無的模型；這些呼叫的費用記為 0 */
  unpriced_models: string[];
};

export type TWorks = {
  id: number;
  image: string;
  style: string;
  element: string;
  color: string;
  space: string;
  image_prompt: string;
};

export const UserAPI = {
  login: async (data: LoginData) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/login/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );

    const res = await response.json();
    if (res.token) {
      return res.token;
    }
    throw new FetchError(response);
  },
  register: async (data: RegisterData) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/register/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );

    const res = await response.json();
    if (res.token) {
      return res.token;
    }
    throw new FetchError(response);
  },
  checkDuplicateGenieName: async (genieName: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/info_duplicate_check/?genie_name=${genieName}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const res = await response.json();
    if (res.status == "ok") {
      return true;
    } else if (res.error || res.status == "bad_request") {
      return false;
    }
    throw new FetchError(response, res.msg);
  },
  checkDuplicateUsername: async (username: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/info_duplicate_check/?username=${username}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const res = await response.json();
    if (res.status == "ok") {
      return true;
    } else if (res.error || res.status == "bad_request") {
      return false;
    }
    throw new FetchError(response, res.msg);
  },
  getUserByLinkWords: async (linkWords: string | undefined) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/designer/${linkWords}/`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const res = await response.json();
    if (res.success) {
      return {
        ...res.data,
        plan: res.plan as TPlan | null,
        custom_styles: res.data.custom_styles
          ? JSON.parse(res.data.custom_styles)
          : {
              theme: "dark",
              primary_color: "#8b7dbe",
              secondary_color: "#FFC700",
            },
        portfolios: (res.portfolios as TPortfolio[]) || [],
      } as TUser;
    }
    throw new FetchError(response, res.msg);
  },
  getUser: async (): Promise<TUser | undefined> => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/personal_info/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site") || new URL(window.location.href).searchParams.get("token")}`,
        },
      },
    );

    const res = await response.json();
    if (res.success) {
      return {
        ...res.data,
        subscription: res.subscription,
        plan: res.plan as TPlan | null,
        custom_styles: res.data.custom_styles
          ? JSON.parse(res.data.custom_styles)
          : {
              theme: "dark",
              primary_color: "#8b7dbe",
              secondary_color: "#FFC700",
            },
        is_superuser: res.is_superuser,
      } as TUser;
    }
    throw new FetchError(response, res.msg);
  },
  updateUser: async (data: FormData) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/personal_info/`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
        body: data,
      },
    );

    const res = await response.json();
    if (res.success) return;
    throw new FetchError(response, res.msg);
  },
  updatePassword: async (data: FormData) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/change_password/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
        body: data,
      },
    );

    const res = await response.json();
    if (res.success) return;
    throw new FetchError(response, res.msg);
  },
  getCreditUsage: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/my/credit_usage/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );

    const res = await response.json();
    if (response.ok) {
      return res.message_count as number;
    }
    throw new FetchError(response, res.msg);
  },
  resetPassword: async (email: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/forget_password/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      },
    );

    if (response.ok) return;
    throw new FetchError(response);
  },
  getDesignerImageGenerationConfig: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/deisnger_imagen_configs/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (response.ok) {
      const res = await response.json();
      return {
        custom_spaces: res.value.custom_spaces,
        custom_themes: res.value.custom_themes,
      } as TDesignerImageGenerationConfig;
    }
    throw new FetchError(response);
  },
  createDesignerImageGenerationConfigTheme: async (
    name: string,
    image_url: string,
    prompt: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/deisnger_imagen_configs/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          display_name: name,
          config_type: "custom_theme",
          data: {
            input_image_url: image_url,
            prompt: prompt,
          },
        }),
      },
    );

    if (response.ok) {
      const res = await response.json();
      return res.value as TCustomTheme;
    }
    throw new FetchError(response);
  },
  updateDesignerImageGenerationConfigSpaces: async (spaces: string[]) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/deisnger_imagen_configs/`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          config_type: "custom_spaces",
          data: spaces,
        }),
      },
    );

    if (response.ok) return;
    throw new FetchError(response);
  },
  updateDesignerImageGenerationConfigTheme: async (
    id: number,
    name: string,
    image_url: string,
    prompt: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/deisnger_imagen_config/${id}/`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          display_name: name,
          data: {
            input_image_url: image_url,
            prompt: prompt,
          },
        }),
      },
    );

    if (response.ok) return;
    throw new FetchError(response);
  },
  deleteDesignerImageGenerationConfigTheme: async (id: number) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/deisnger_imagen_config/${id}/`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
          Accept: "*/*",
        },
      },
    );

    if (response.ok) return;
    throw new FetchError(response);
  },
  getAccountWorks: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/personal_work/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );

    if (response.ok) {
      const res = await response.json();
      return res.data as TWorks[];
    }
    throw new FetchError(response);
  },
  getAccountWorksById: async (workId: number) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/personal_work/${workId}/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );
    if (response.ok) {
      const res = await response.json();
      return res.data as TWorks;
    }
    throw new FetchError(response);
  },
  updateAccountWorks: async (workId: number, data: FormData) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/personal_work/${workId}/`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
        body: data,
      },
    );
    if (response.ok) return;
    throw new FetchError(response);
  },
  deleteAccountWorks: async (workId: number) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/personal_work/${workId}/`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );
    if (response.ok) return;
    throw new FetchError(response);
  },
  createAccountWorks: async (data: FormData) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/personal_work/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
        body: data,
      },
    );
    if (response.ok) return;
    throw new FetchError(response);
  },
  getAccountPortfolioList: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/portfolio/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );
    if (response.ok) {
      const res = await response.json();
      return res.data as TPortfolio[];
    }
    throw new FetchError(response);
  },
  getAccountPortfolioById: async (portfolioId: number) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/portfolio/${portfolioId}/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );
    if (response.ok) {
      const res = await response.json();
      return res.data as TPortfolio;
    }
    throw new FetchError(response);
  },
  createAccountPortfolio: async (data: FormData) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/portfolio/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
        body: data,
      },
    );
    if (response.ok) return;
    throw new FetchError(response);
  },
  updateAccountPortfolio: async (portfolioId: number, data: FormData) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/portfolio/${portfolioId}/`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
        body: data,
      },
    );
    if (response.ok) return;
    throw new FetchError(response);
  },
  deleteAccountPortfolio: async (portfolioId: number) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/portfolio/${portfolioId}/`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );
    if (response.ok) return;
    throw new FetchError(response);
  },
  analyzeImageByData: async (
    image: string,
    file_type: string,
    prompt: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/image_analysis/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file: {
            data: image,
            file_type: file_type,
          },
          prompt: prompt,
        }),
      },
    );
    if (response.ok) {
      const res = await response.json();
      return res.data.analysis as string;
    }
    throw new FetchError(response);
  },
  analyzeImageByUrl: async (url: string, prompt: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/image_analysis/`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url: url,
          prompt: prompt,
        }),
      },
    );
    if (response.ok) {
      const res = await response.json();
      return res.data.analysis as string;
    }
    throw new FetchError(response);
  },
  getClientRequestsStatistics: async (days: number, sort: string = "total") => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/client_requests/statistics/?days=${days}&sort=${sort}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );
    if (response.ok) {
      const res = await response.json();
      return res.data as TClientRequestsStatistics[];
    }
    throw new FetchError(response);
  },
  getAiUsageMonthly: async (months: number = 6) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/ai_usage/monthly/?months=${months}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );
    if (response.ok) {
      const res = await response.json();
      return res.data as TAiUsageMonthly;
    }
    throw new FetchError(response);
  },
  downloadAllUsersInformation: async () => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_ENDPOINT}/accounts/users_info_csv`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("site")}`,
        },
      },
    );
    if (response.ok) {
      const blob = await response.blob();
      return blob;
    }
    throw new FetchError(response);
  },
};
